import { INCIDENT_RESOLUTION_LABELS, INCIDENT_STATE_LABELS, type IncidentOptions, type IncidentResolution, type IncidentState } from '@lia/contracts';
import * as React from 'react';
import { cn } from '../../lib/cn.js';
import { StatusBadge, type StatusTone } from '../primitives/badge.js';
import { Button } from '../primitives/button.js';
import { FormField } from '../primitives/form-field.js';
import { Textarea } from '../primitives/input.js';

// Incidents with a named owner (setter spec §05.19, §05.20): state badge and the resolution picker. LIA only shows the
// options it computed; nothing is applied until the responsible person (or management) picks one and confirms.

export const INCIDENT_STATE_TONE: Record<IncidentState, StatusTone> = {
  open: 'warning',
  acknowledged: 'info',
  escalated: 'danger',
  resolved: 'success',
};

export function IncidentStateBadge({ state, overdue, className }: { state: IncidentState; overdue?: boolean; className?: string }) {
  return (
    <span className={cn('inline-flex flex-wrap items-center gap-1', className)}>
      <StatusBadge tone={INCIDENT_STATE_TONE[state]}>{INCIDENT_STATE_LABELS[state]}</StatusBadge>
      {overdue && state !== 'resolved' ? <StatusBadge tone="danger">Sin tomar a tiempo</StatusBadge> : null}
    </span>
  );
}

export interface IncidentResolutionFormProps {
  options: IncidentOptions;
  onResolve: (resolution: IncidentResolution, note: string, replacementMembershipId: string | null) => void;
  busy?: boolean;
  /** Whole control disabled (e.g. not the responsible person), with the reason shown. */
  disabledReason?: string | null;
  className?: string;
}

const ORDER: readonly IncidentResolution[] = ['replace_advisor', 'keep_time', 'reschedule', 'other'];

export function IncidentResolutionForm({ options, onResolve, busy, disabledReason, className }: IncidentResolutionFormProps) {
  const [picked, setPicked] = React.useState<IncidentResolution | null>(null);
  const [replacement, setReplacement] = React.useState<string | null>(null);
  const [note, setNote] = React.useState('');
  const name = React.useId();
  const noteId = React.useId();
  const disabled = !!disabledReason || !!busy;
  const detail: Record<IncidentResolution, { available: boolean; detail: string }> = {
    replace_advisor: options.replaceAdvisor,
    keep_time: options.keepTime,
    reschedule: options.reschedule,
    other: { available: true, detail: 'Registra la decisión acordada (por ejemplo, la gerencia atiende la cita).' },
  };
  const ready = picked && !(picked === 'replace_advisor' && !replacement) && !(picked === 'other' && !note.trim());
  return (
    <div className={cn('grid gap-3', className)}>
      <fieldset className="grid gap-2" disabled={disabled}>
        <legend className="mb-1 text-body font-medium text-text-primary">Solución</legend>
        {ORDER.map((r) => {
          const d = detail[r];
          const id = `${name}-${r}`;
          return (
            <div key={r} className={cn('grid gap-1 rounded-card border border-border-subtle p-3', picked === r && 'border-brand-primary bg-surface-subtle', !d.available && 'opacity-60')}>
              <label htmlFor={id} className="flex items-center gap-2 text-body text-text-primary">
                <input id={id} type="radio" name={name} value={r} checked={picked === r} disabled={disabled || !d.available} onChange={() => setPicked(r)} className="accent-brand-primary" />
                {INCIDENT_RESOLUTION_LABELS[r]}
              </label>
              <p className="text-meta text-text-secondary">{d.detail}</p>
              {r === 'replace_advisor' && picked === r ? (
                <div role="radiogroup" aria-label="Asesor que atenderá la cita" className="grid gap-1 pl-6">
                  {options.replaceAdvisor.candidates.map((c) => (
                    <label key={c.membershipId} className="flex flex-wrap items-center gap-2 text-body text-text-primary">
                      <input type="radio" name={`${name}-candidate`} value={c.membershipId} checked={replacement === c.membershipId} onChange={() => setReplacement(c.membershipId)} className="accent-brand-primary" />
                      {c.name}
                      <StatusBadge tone={c.calendlyHost === 'yes' ? 'info' : 'neutral'}>{c.calendlyHost === 'yes' ? 'Anfitrión en Calendly' : 'Disponibilidad sin verificar'}</StatusBadge>
                      {!c.hasWhatsapp ? <span className="text-meta text-text-secondary">sin WhatsApp registrado</span> : null}
                    </label>
                  ))}
                </div>
              ) : null}
            </div>
          );
        })}
      </fieldset>
      {disabledReason ? <p className="text-meta text-text-secondary">{disabledReason}</p> : null}
      {picked && !disabled ? (
        <div className="grid gap-2">
          <FormField id={noteId} label={picked === 'other' ? 'Decisión acordada' : 'Nota (opcional)'} required={picked === 'other'} description="Se guarda en la incidencia y en el CRM.">
            <Textarea id={noteId} rows={2} maxLength={1000} value={note} onChange={(e) => setNote(e.target.value)} />
          </FormField>
          <div className="flex flex-wrap gap-2">
            <Button type="button" size="sm" disabled={!ready} onClick={() => picked && onResolve(picked, note.trim(), picked === 'replace_advisor' ? replacement : null)}>
              Aplicar y resolver
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={() => setPicked(null)}>
              Cancelar
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
